import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link as MLink,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import BillingBreakdownDisplay, { BillingBreakdownLine } from '../components/BillingBreakdownDisplay';
import { userCanAccessFirm } from '../utils/firmAccess';

interface ClientFirmOption {
  id: number;
  name: string;
}

interface ClientEstimate {
  id: number;
  case_id: number;
  client_firm_id?: number;
  billing_type: string;
  quantity: number;
  total_pages?: number | null;
  reviewed_pages?: number | null;
  appendix_included?: boolean;
  total_amount: number;
  status: string;
  created_at: string;
  is_invoiced?: boolean;
  billing_breakdown?: { lines: BillingBreakdownLine[] } | null;
  clientFirm?: { id: number; name: string } | null;
  case?: {
    id: number;
    title: string;
    subtitle?: string;
    project?: { id: number; name: string; firm_id?: number };
  };
  createdBy?: { id: number; name: string };
}

const ClientEstimatesList: React.FC = () => {
  const { firmId } = useParams<{ firmId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [estimates, setEstimates] = useState<ClientEstimate[]>([]);
  const [firms, setFirms] = useState<ClientFirmOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [firmName, setFirmName] = useState('');
  const [firmFilter, setFirmFilter] = useState(firmId || 'all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const isClientUser = () => {
    if (user?.is_finance_user) return true;
    if (!user?.roles) return false;
    return user.roles.some((role) =>
      ['firm_admin', 'firm_main', 'client_admin', 'client_manager', 'client_finance_user'].includes(role.slug)
    );
  };

  const isPmpAdmin = () => user?.role === 'pmp_admin' || (user?.roles?.some((role) => role.slug === 'pmp_admin') ?? false);
  const isSiteAdmin = () => user?.role === 'site_admin' || (user?.roles?.some((role) => role.slug === 'site_admin') ?? false);
  const canViewBreakdown = () => isPmpAdmin() || isClientUser();
  const canAccessThisFirm = !firmId || isPmpAdmin() || (!isSiteAdmin() && userCanAccessFirm(user, firmId));

  useEffect(() => {
    loadFirms();
    if (firmId) {
      setFirmFilter(firmId);
      loadFirmName();
    }
  }, [firmId]);

  useEffect(() => {
    loadEstimates();
  }, [firmFilter, statusFilter]);

  const loadFirms = async () => {
    try {
      const response = await api.get('/firms', { params: { type: 'client', per_page: 500 } });
      const payload = response.data?.data;
      const rows = Array.isArray(payload) ? payload : payload?.data || [];
      const options = rows
        .map((firm: ClientFirmOption) => ({ id: Number(firm.id), name: firm.name }))
        .filter((firm: ClientFirmOption) => firm.id && firm.name)
        .sort((a: ClientFirmOption, b: ClientFirmOption) => a.name.localeCompare(b.name));
      setFirms(options);
    } catch {
      // The table still shows each estimate's firm when the firm list cannot load.
    }
  };

  const loadFirmName = async () => {
    try {
      const response = await api.get(`/firms/${firmId}`);
      if (response.data.success) {
        setFirmName(response.data.data.name);
      }
    } catch {
      // ignore
    }
  };

  const loadEstimates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/client-estimates', {
        params: {
          status: statusFilter,
          ...(firmFilter !== 'all' ? { firm_id: firmFilter } : {}),
        },
      });
      if (response.data.success) {
        setEstimates(response.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load estimates');
    } finally {
      setLoading(false);
    }
  };

  const estimateFirmId = (estimate: ClientEstimate) =>
    estimate.clientFirm?.id || estimate.client_firm_id || estimate.case?.project?.firm_id;

  const estimateFirmName = (estimate: ClientEstimate) => {
    if (estimate.clientFirm?.name) return estimate.clientFirm.name;
    const id = estimateFirmId(estimate);
    return firms.find((firm) => firm.id === Number(id))?.name || '—';
  };

  const openCase = (estimate: ClientEstimate) => {
    const caseFirmId = estimateFirmId(estimate);
    const caseId = estimate.case?.id || estimate.case_id;
    if (!caseFirmId || !caseId) return;
    navigate(`/firms/${caseFirmId}/cases/${caseId}`);
  };

  const getBillingTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      page_based: 'Page Based',
      consolidation: 'Consolidation',
      time_based: 'Time Based',
      case_based: 'Case Based',
      flat_rate: 'Flat Rate',
    };
    return labels[type] || type;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'default',
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
    };
    return colors[status] || 'default';
  };

  const firmOptions = firms.some((firm) => String(firm.id) === String(firmFilter)) || firmFilter === 'all'
    ? firms
    : [{ id: Number(firmFilter), name: firmName || `Firm #${firmFilter}` }, ...firms];

  const columnCount = canViewBreakdown() ? 9 : 8;

  if (isSiteAdmin() && !isPmpAdmin()) {
    return (
      <Box>
        <Alert severity="error">You don't have permission to access this page.</Alert>
      </Box>
    );
  }

  if (!isClientUser() && !isPmpAdmin()) {
    return (
      <Box>
        <Alert severity="error">You don't have permission to access this page.</Alert>
      </Box>
    );
  }

  if (!canAccessThisFirm) {
    return (
      <Box>
        <Alert severity="error">You can only view estimates for your own firm.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {firmId && (
        <Breadcrumbs sx={{ mb: 2 }}>
          <MLink color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate('/firms'); }} sx={{ textDecoration: 'none' }}>
            Firms
          </MLink>
          <MLink color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate(`/firms/${firmId}`); }} sx={{ textDecoration: 'none' }}>
            {firmName || `Firm #${firmId}`}
          </MLink>
          <Typography color="text.primary">Client Estimates</Typography>
        </Breadcrumbs>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4">All Estimates</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Firm</InputLabel>
            <Select value={firmFilter} label="Firm" onChange={(e) => setFirmFilter(e.target.value)}>
              <MenuItem value="all">All firms</MenuItem>
              {firms.map((firm) => (
                <MenuItem key={firm.id} value={String(firm.id)}>
                  {firm.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="all">All</MenuItem>
              {isPmpAdmin() && <MenuItem value="draft">Draft</MenuItem>}
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : estimates.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">No estimates found</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Case</strong></TableCell>
                <TableCell><strong>Firm</strong></TableCell>
                <TableCell><strong>Project</strong></TableCell>
                <TableCell><strong>Billing Type</strong></TableCell>
                <TableCell><strong>Pages</strong></TableCell>
                {canViewBreakdown() && <TableCell><strong>Total</strong></TableCell>}
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Created</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {estimates.map((estimate) => (
                <React.Fragment key={estimate.id}>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{estimate.case?.title || `Case #${estimate.case_id}`}</Typography>
                    </TableCell>
                    <TableCell>{estimateFirmName(estimate)}</TableCell>
                    <TableCell>{estimate.case?.project?.name || '—'}</TableCell>
                    <TableCell>{getBillingTypeLabel(estimate.billing_type)}</TableCell>
                    <TableCell>
                      {estimate.total_pages != null ? (
                        <>
                          {estimate.total_pages} total
                          {estimate.reviewed_pages != null && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {estimate.reviewed_pages} reviewed
                            </Typography>
                          )}
                          {estimate.appendix_included && (
                            <Typography variant="caption" display="block" color="text.secondary">Appendix</Typography>
                          )}
                        </>
                      ) : (
                        estimate.quantity
                      )}
                    </TableCell>
                    {canViewBreakdown() && (
                      <TableCell>${Number(estimate.total_amount).toFixed(2)}</TableCell>
                    )}
                    <TableCell>
                      <Chip label={estimate.status.toUpperCase()} color={getStatusColor(estimate.status) as any} size="small" />
                      {estimate.is_invoiced && <Chip label="INVOICED" color="info" size="small" sx={{ ml: 0.5 }} />}
                    </TableCell>
                    <TableCell>{new Date(estimate.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => openCase(estimate)} disabled={!estimateFirmId(estimate)}>
                        View Case
                      </Button>
                      {canViewBreakdown() && estimate.billing_breakdown?.lines?.length ? (
                        <Button size="small" onClick={() => setExpandedId(expandedId === estimate.id ? null : estimate.id)}>
                          {expandedId === estimate.id ? 'Hide' : 'Breakdown'}
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                  {expandedId === estimate.id && estimate.billing_breakdown?.lines?.length ? (
                    <TableRow>
                      <TableCell colSpan={columnCount}>
                        <BillingBreakdownDisplay
                          lines={estimate.billing_breakdown.lines}
                          totalAmount={Number(estimate.total_amount)}
                        />
                      </TableCell>
                    </TableRow>
                  ) : null}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ClientEstimatesList;
